import { motion } from "framer-motion";
import { Palette, Globe, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";

const services = [
  {
    icon: Palette,
    title: "High-Performance Web Design",
    description:
      "Next.js and Tailwind CSS architectures with 3D interactive showcases using Three.js. Immediate loading states, near-perfect Lighthouse scores, and conversion-focused layouts.",
    features: ["Next.js / Vite", "Tailwind CSS 4", "Three.js Showcases", "95-100 Lighthouse"],
    color: "wasabi",
  },
  {
    icon: Globe,
    title: "Brand Ecosystem Development",
    description:
      "End-to-end digital presence for restaurants and businesses. From Oasis to Red Spice — unified design systems that scale multi-page brand ecosystems rapidly.",
    features: ["Restaurant Suites", "Multi-Page Systems", "SEO Architecture", "Mobile-First"],
    color: "mint",
  },
  {
    icon: Sparkles,
    title: "Content & Motion Strategy",
    description:
      "Visual-first storytelling through the Viral Cutz identity. Cinematic brand presentation with high-impact motion graphics integrated directly into the web UX.",
    features: ["Viral Cutz Brand", "Motion Graphics", "DaVinci Resolve", "Social Media Growth"],
    color: "wasabi",
  },
];

const stats = [
  { value: "5+", label: "BRANDS SHIPPED" },
  { value: "95+", label: "LIGHTHOUSE SCORES" },
  { value: "−40%", label: "LOAD TIME CUT" },
  { value: "100%", label: "MOBILE FLUID" },
];

export const AxiomDigitalStudio = () => {
  return (
    <section id="studio" className="pt-16 pb-24 px-6 relative overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 data-grid opacity-40" />

      {/* Floating gradient orbs */}
      <div
        className="absolute top-20 left-10 w-[300px] h-[300px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, hsl(68 100% 50% / 0.04), transparent 70%)",
          animation: "floatOrb 8s ease-in-out infinite",
        }}
      />
      <div
        className="absolute bottom-20 right-10 w-[250px] h-[250px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, hsl(162 100% 50% / 0.04), transparent 70%)",
          animation: "floatOrb 10s ease-in-out infinite reverse",
        }}
      />

      <div className="max-w-7xl mx-auto relative">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-20">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <div className="eyebrow mb-3">// AXIOM_DIGITAL_STUDIO</div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl text-foreground mb-5 leading-tight">
              The{" "}
              <span className="text-gradient-wasabi-mint">Digital Presence</span>
              <br />Arm
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed max-w-md">
              Axiom Digital bridges complex engineering with market-ready commercial
              interfaces. Transforming high-level brand identities into performant,
              high-conversion web ecosystems.
            </p>

            <a
              href="https://github.com/Axiom-Digital-Studios"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-wasabi mt-8 px-7 py-3.5 rounded-xl text-sm uppercase tracking-widest inline-flex items-center gap-2"
            >
              VIEW STUDIO <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>

          {/* 3D Cube + Stats */}
          <motion.div
            initial={{ opacity: 0, x: 32 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="flex flex-col items-center gap-8"
          >
            {/* 3D Cube visual */}
            <div className="relative flex items-center justify-center">
              {/* Glow ring */}
              <div
                className="absolute rounded-full"
                style={{
                  width: "220px",
                  height: "220px",
                  background: "radial-gradient(circle, hsl(68 100% 50% / 0.08), transparent 70%)",
                  animation: "pulse-glow 4s ease-in-out infinite",
                }}
              />
              <div className="cube-scene flex items-center justify-center" style={{ width: 180, height: 180 }}>
                <div className="cube" style={{ width: 140, height: 140 }}>
                  <div className="cube-face cube-face-front" />
                  <div className="cube-face cube-face-back" />
                  <div className="cube-face cube-face-left" />
                  <div className="cube-face cube-face-right" />
                  <div className="cube-face cube-face-top" />
                  <div className="cube-face cube-face-bottom" />
                  {/* Inner cube */}
                  <div
                    style={{
                      position: "absolute",
                      width: 70,
                      height: 70,
                      top: "50%",
                      left: "50%",
                      transformOrigin: "center",
                      transform: "translate(-50%,-50%)",
                      transformStyle: "preserve-3d",
                      animation: "rotateCube 8s linear infinite reverse",
                    }}
                  >
                    {["front","back","left","right","top","bottom"].map((face) => (
                      <div
                        key={face}
                        style={{
                          position: "absolute",
                          width: 70,
                          height: 70,
                          border: "1px solid hsl(162 100% 50% / 0.5)",
                          background: "hsl(162 100% 50% / 0.03)",
                          transform: {
                            front: "translateZ(35px)",
                            back: "translateZ(-35px) rotateY(180deg)",
                            left: "translateX(-35px) rotateY(-90deg)",
                            right: "translateX(35px) rotateY(90deg)",
                            top: "translateY(-35px) rotateX(90deg)",
                            bottom: "translateY(35px) rotateX(-90deg)",
                          }[face],
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-3 w-full max-w-xs">
              {stats.map(({ value, label }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.2 + i * 0.1 }}
                  className="glass-card rounded-xl p-4 text-center"
                >
                  <div className="font-display font-bold text-2xl text-primary mb-1">{value}</div>
                  <div className="font-mono text-xs text-muted-foreground tracking-wider">{label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Services */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.12 } } }}
          className="grid grid-cols-1 md:grid-cols-3 gap-5"
        >
          {services.map((svc) => {
            const Icon = svc.icon;
            const isWasabi = svc.color === "wasabi";
            return (
              <motion.div
                key={svc.title}
                variants={{ hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } } }}
                className={`bento-card p-7 flex flex-col ${
                  isWasabi ? "glass-card-wasabi" : "glass-card-mint"
                }`}
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center mb-5 ${
                    isWasabi ? "bg-primary/15" : "bg-accent/15"
                  }`}
                >
                  <Icon
                    className={`w-5 h-5 ${isWasabi ? "text-primary" : "text-accent"}`}
                  />
                </div>
                <h3 className="font-display font-semibold text-xl text-foreground mb-3">
                  {svc.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                  {svc.description}
                </p>
                <ul className="space-y-2 mt-auto">
                  {svc.features.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <CheckCircle2
                        className={`w-3.5 h-3.5 flex-shrink-0 ${
                          isWasabi ? "text-primary" : "text-accent"
                        }`}
                      />
                      <span className="font-mono text-xs text-muted-foreground">{f}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default AxiomDigitalStudio;
